import './LinearBuyingAssignments.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UpdateLinearBuyingAssignmentsParams } from '@blisspointmedia/bpm-types/dist/LinearAssignments';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import { SaveChangesInput } from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useSetError } from '@src/redux/modals';
import {
  saveLinearBuyingAssignments,
  useLinearBuyingAssignments
} from './use-linear-buying-assignments';

type AssignmentRow = UpdateLinearBuyingAssignmentsParams;

const headers = [
  {
    label: 'Company',
    field: 'company',
    type: 'select',
    options: 'companiesConstantOnSet',
    flex: 1,
    modalRow: 0,
    modalWidth: 375,
    isClearable: false
  },
  {
    label: 'Buyer Email',
    field: 'email',
    type: 'select',
    options: 'emails',
    flex: 1,
    modalRow: 0,
    modalWidth: 375,
    isClearable: false
  }
];

const toRow = ({ company, email }: AssignmentRow): AssignmentRow => ({ company, email });

export const LinearBuyingAssignments = () => {
  const setError = useSetError();
  const { data, isLoading, error } = useLinearBuyingAssignments();
  // Post-save working copy; undefined until the first successful save.
  const [savedRows, setSavedRows] = useState<AssignmentRow[]>();

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to get linear buying assignments data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const rows = useMemo(() => savedRows ?? data?.assignments.map(toRow), [savedRows, data]);

  const selectorOptions = useMemo(() => {
    const assigned = new Set(rows?.map(row => row.company));
    return {
      companiesConstantOnSet: (data?.companies ?? [])
        .filter(({ cid }) => !assigned.has(cid))
        .map(({ cid }) => ({ label: cid, value: cid })),
      emails: (data?.contacts ?? []).map(({ email }) => ({ label: email, value: email }))
    };
  }, [rows, data]);

  const handleSave = useCallback(
    async ({ addedData, modifiedData, cleanup }: SaveChangesInput<AssignmentRow>) => {
      const changed = [...addedData, ...modifiedData].map(toRow);
      try {
        await saveLinearBuyingAssignments(changed);
        const changedByCompany = new Map(changed.map(row => [row.company, row]));
        const existing = (rows ?? []).map(row => changedByCompany.get(row.company) ?? row);
        const existingCompanies = new Set(existing.map(row => row.company));
        setSavedRows([...existing, ...changed.filter(row => !existingCompanies.has(row.company))]);
        cleanup();
      } catch (e) {
        setError({
          message: `Failed to set linear buying assignments data: ${e.message}`,
          reportError: e
        });
      }
    },
    [rows, setError]
  );

  return (
    <Page title='Linear Buying Assignments' pageType='Linear Buying Assignments' minHeight='600px'>
      <div className='linear-buying-assignments-page-container'>
        <ModalEditWidget<AssignmentRow>
          className='linear-buying-assignments-table'
          name='Assignments'
          headers={headers}
          initialTableData={rows ?? []}
          selectorOptions={selectorOptions}
          onSaveModal={handleSave}
          filterBar
          enableDelete={false}
          isLoading={isLoading}
          emptyContent={error ? <TableEmptyState message="Couldn't load data" /> : undefined}
        />
      </div>
    </Page>
  );
};
